<template>
   <div>
      <navbar-component />
      <main class="bg-blueGray-100">
         <!-- Hero -->
         <section class="relative overflow-hidden pt-16 py-20 md:py-28" :style="{ backgroundColor: '#111827' }">
            <div class="absolute inset-0 bg-center bg-cover" :style="{ backgroundImage: `url(${heroImage})`, opacity: 0.35 }"></div>
            <div class="absolute inset-0" :style="{ backgroundColor: 'rgba(0, 0, 0, 0.72)' }"></div>
            <div class="container relative mx-auto px-4 md:px-6">
               <div class="max-w-3xl">
                  <span class="inline-block bg-blue-600 text-white text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-5 shadow-sm">
                     Datavan Pump Control
                  </span>
                  <h1 class="text-white font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-5">
                     Introducing the Redesigned Datavan MPC
                  </h1>
                  <p class="text-blueGray-100 text-lg md:text-xl leading-relaxed max-w-2xl mb-4">
                     Pump control for Pump Down jobs and full fleets—up to 20 pumps per 4K monitor.
                  </p>
                  <p class="text-blueGray-300 text-base md:text-lg leading-relaxed max-w-2xl mb-8">
                     A multipump control station for Pump Down and large-pad operations that unifies WaveEdge, PumpLink7, and legacy pumps—with streaming to WaveData Cloud for remote monitoring and operation.
                  </p>
                  <div class="flex flex-wrap gap-3">
                     <router-link
                        :to="{ path: '/', hash: '#contact' }"
                        class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                     >
                        Book a Demo <i class="fas fa-arrow-right ml-2"></i>
                     </router-link>
                     <router-link to="/wavedata" class="inline-block bg-transparent hover:bg-blueGray-800 text-white font-bold py-3 px-6 rounded-lg border border-blueGray-500 transition-colors duration-200">
                        See WaveData Cloud
                     </router-link>
                  </div>
               </div>
            </div>
         </section>

         <!-- UI Showcase -->
         <section id="showcase" class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">Crystal-clear pump control</h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     Runs locally in the field for reliable on-site control—optimized for larger screens and 4K monitors, with the same experience on mobile—while integrating with WaveData Cloud so you can view and operate from anywhere.
                  </p>
               </div>
               <div class="max-w-6xl mx-auto flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-8">
                  <div class="w-full lg:w-3/4">
                     <!-- Single image fade: swap only while hidden so nothing stacks -->
                     <div class="rounded-xl overflow-hidden border border-blueGray-700 shadow-lg bg-blueGray-900">
                        <img
                           :src="showcaseSlides[showcaseIndex].image"
                           :alt="showcaseSlides[showcaseIndex].alt"
                           class="w-full h-auto block transition-opacity duration-700 ease-in-out"
                           :style="{ opacity: showcaseVisible ? 1 : 0 }"
                        />
                     </div>
                     <p class="mt-3 text-center text-sm text-blueGray-400 tracking-wide">
                        {{ showcaseSlides[showcaseIndex].label }}
                     </p>
                     <!-- Preload both shots so the swap is instant -->
                     <img
                        v-for="(slide, i) in showcaseSlides"
                        :key="'preload-' + i"
                        :src="slide.image"
                        alt=""
                        class="hidden"
                        aria-hidden="true"
                     />
                  </div>
                  <div class="w-48 sm:w-56 md:w-64 flex-shrink-0 rounded-xl overflow-hidden border border-blueGray-700 shadow-lg">
                     <img :src="mobileImage" alt="Datavan MPC mobile pump control interface" class="w-full h-auto" />
                  </div>
               </div>
            </div>
         </section>

         <!-- Operator capabilities -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <!-- Field scale callout -->
               <div class="max-w-4xl mx-auto rounded-xl p-6 md:p-8 border border-blue-500/60 bg-blueGray-800 shadow-lg mb-10 md:mb-12">
                  <div class="flex flex-col sm:flex-row items-start sm:items-center gap-4 md:gap-5">
                     <div class="w-12 h-12 rounded-full bg-blue-900 text-lightBlue-300 border-2 border-blue-500 flex items-center justify-center flex-shrink-0">
                        <i class="fas fa-industry"></i>
                     </div>
                     <div>
                        <h2 class="text-xl md:text-2xl font-bold text-white mb-2">Built for the pad—any size</h2>
                        <p class="text-blueGray-300 text-base md:text-lg leading-relaxed">
                           Use Datavan MPC for Pump Down customers with just one or two pumps, or scale up to full data-van fleet pump control with
                           <span class="text-lightBlue-300 font-semibold">up to 40+ pumps</span>
                           across multiple monitors. From Pump Down pads to large fleets—one station.
                        </p>
                     </div>
                  </div>
               </div>
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">Built for the data van operator</h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     Pump and Summary views give you individual pump control and fleet-wide commands in one station.
                  </p>
                  <div class="w-24 h-1 rounded bg-blue-500 mx-auto mt-5"></div>
               </div>
               <div class="max-w-5xl mx-auto rounded-2xl border border-blueGray-700 bg-blueGray-800 overflow-hidden shadow-lg">
                  <div class="overflow-x-auto">
                     <table class="w-full text-left border-collapse">
                        <thead>
                           <tr class="border-b border-blueGray-700">
                              <th class="px-3 py-3 text-xs font-semibold tracking-wide text-blueGray-400 text-center" style="width: 7rem">Category</th>
                              <th class="py-3 text-xs font-semibold tracking-wide text-blueGray-400" style="width: 30%; padding-left: 1.5rem; padding-right: 1.25rem">Capability</th>
                              <th class="px-5 py-3 text-xs font-semibold tracking-wide text-blueGray-400">What you can do</th>
                           </tr>
                        </thead>
                        <tbody>
                           <tr
                              v-for="row in operatorFeatureRows"
                              :key="row.key"
                              class="border-b border-blueGray-700 last:border-b-0 hover:bg-blueGray-900 transition-colors duration-150"
                           >
                              <td
                                 v-if="row.showCategory"
                                 :rowspan="row.rowspan"
                                 class="px-3 py-4 align-middle border-r border-blueGray-700 bg-blueGray-900"
                              >
                                 <div class="flex flex-col items-center justify-center gap-2 text-center">
                                    <i :class="[row.categoryIcon, row.colorClass]" class="text-3xl leading-none"></i>
                                    <span
                                       class="text-xs font-bold uppercase tracking-wider leading-tight"
                                       :class="row.colorClass"
                                    >{{ row.category }}</span>
                                 </div>
                              </td>
                              <td class="py-4 align-middle" style="padding-left: 1.5rem; padding-right: 1.25rem">
                                 <span class="font-semibold text-white text-base leading-snug">{{ row.capability }}</span>
                              </td>
                              <td class="px-5 py-4 text-blueGray-300 text-sm md:text-base leading-relaxed align-middle">
                                 {{ row.description }}
                              </td>
                           </tr>
                        </tbody>
                     </table>
                  </div>
               </div>
            </div>
         </section>

         <!-- Key Features -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">Key features and benefits</h2>
                  <p class="text-lg text-blueGray-300">
                     Built for usability, scale, and remote operations.
                  </p>
               </div>
               <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  <div
                     v-for="feature in features"
                     :key="feature.title"
                     class="bg-blueGray-800 rounded-xl p-5 md:p-6 shadow-sm border border-blueGray-700"
                  >
                     <div class="flex items-center gap-3 mb-3">
                        <div class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border-2" :class="feature.iconWrapClass">
                           <i :class="feature.icon"></i>
                        </div>
                        <h3 class="text-xl font-bold text-white leading-tight">{{ feature.title }}</h3>
                     </div>
                     <p class="text-blueGray-300 text-sm md:text-base leading-relaxed">{{ feature.text }}</p>
                  </div>
               </div>
            </div>
         </section>

         <!-- Remote Monitoring & Control -->
         <section class="py-16 md:py-20 bg-blueGray-100">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-blueGray-800 mb-3">Remote monitoring and control</h2>
                  <p class="text-lg text-blueGray-600 max-w-3xl mx-auto">
                     Manage your fleet from anywhere. Local control becomes a global capability through WaveData Cloud.
                  </p>
               </div>
               <div class="grid lg:grid-cols-2 gap-6 lg:gap-8 mb-10">
                  <div class="bg-white rounded-xl overflow-hidden border border-blueGray-200 shadow-md h-full flex flex-col">
                     <img :src="remoteImage" alt="Remote monitoring from desktop and mobile" class="w-full h-48 md:h-56 object-cover object-top" />
                     <div class="p-6 md:p-7 flex-grow">
                        <h3 class="text-2xl font-bold text-blueGray-800 mb-2">From the data van to the field</h3>
                        <p class="text-blueGray-600 leading-relaxed">
                           Stream critical operational data into WaveData Cloud for comprehensive remote monitoring—and remote pump operation when you need to intervene without being on site.
                        </p>
                     </div>
                  </div>
                  <div class="bg-white rounded-xl overflow-hidden border border-blueGray-200 shadow-md h-full flex flex-col">
                     <img :src="summaryImage" alt="WaveData Cloud fleet monitoring view" class="w-full h-48 md:h-56 object-cover object-top" />
                     <div class="p-6 md:p-7 flex-grow">
                        <h3 class="text-2xl font-bold text-blueGray-800 mb-2">Complete fleet visibility</h3>
                        <p class="text-blueGray-600 leading-relaxed">
                           Go beyond individual pump control with cloud and fleet monitoring that gives a holistic view of system performance and health across your operation.
                        </p>
                     </div>
                  </div>
               </div>
               <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                  <div
                     v-for="prop in remoteProps"
                     :key="prop.title"
                     class="bg-white rounded-xl p-5 border border-blueGray-200 shadow-sm text-center"
                  >
                     <div class="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mx-auto mb-3">
                        <i :class="prop.icon"></i>
                     </div>
                     <h4 class="font-bold text-blueGray-800 text-sm md:text-base">{{ prop.title }}</h4>
                  </div>
               </div>
            </div>
         </section>

         <!-- One Platform -->
         <section class="py-16 md:py-20 bg-blueGray-800">
            <div class="container mx-auto px-4 md:px-6">
               <div class="text-center max-w-4xl mx-auto mb-10 md:mb-12">
                  <h2 class="text-3xl md:text-4xl font-bold text-white mb-3">
                     One platform. <span class="text-lightBlue-300">New iron and the fleet you already run.</span>
                  </h2>
                  <p class="text-lg text-blueGray-300 max-w-3xl mx-auto">
                     MPC brings together AMI’s modern edge stack while protecting your existing investment.
                  </p>
               </div>
               <div class="grid md:grid-cols-3 gap-5 md:gap-6 mb-8">
                  <router-link
                     v-for="pillar in platformPillars"
                     :key="pillar.title"
                     :to="pillar.to"
                     class="bg-blueGray-900 rounded-xl p-6 md:p-7 border border-blueGray-700 shadow-sm hover:border-blue-500 transition-colors duration-200 block"
                  >
                     <div class="w-12 h-12 rounded-full flex items-center justify-center mb-4 border-2" :class="pillar.iconWrapClass">
                        <i :class="pillar.icon"></i>
                     </div>
                     <h3 class="text-xl font-bold text-white mb-1">{{ pillar.title }}</h3>
                     <p class="text-sm text-blueGray-400 mb-2">{{ pillar.subtitle }}</p>
                     <p class="text-blueGray-300 text-sm md:text-base">{{ pillar.text }}</p>
                     <p class="mt-4 text-blue-400 text-sm font-semibold">
                        Learn more <i class="fas fa-arrow-right ml-1"></i>
                     </p>
                  </router-link>
               </div>
               <div class="bg-blueGray-900 rounded-xl p-6 md:p-8 border border-emerald-600/50 text-center max-w-4xl mx-auto">
                  <div class="flex flex-col sm:flex-row items-center justify-center gap-3">
                     <div class="w-12 h-12 rounded-full bg-emerald-900 text-emerald-300 border-2 border-emerald-500 flex items-center justify-center flex-shrink-0">
                        <i class="fas fa-shield-alt"></i>
                     </div>
                     <p class="text-xl md:text-2xl font-bold text-white">
                        Full compatibility with <span class="text-emerald-300">legacy pumps</span>.
                     </p>
                  </div>
               </div>
            </div>
         </section>

         <!-- AMI Advantage -->
         <section class="py-16 md:py-20 bg-white">
            <div class="container mx-auto px-4 md:px-6">
               <div class="max-w-3xl mx-auto text-center">
                  <div class="w-14 h-14 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mx-auto mb-5">
                     <i class="fas fa-award text-2xl"></i>
                  </div>
                  <h2 class="text-3xl md:text-4xl font-bold text-blueGray-800 mb-4">The AMI advantage</h2>
                  <p class="text-lg text-blueGray-600 leading-relaxed mb-2">
                     Over <span class="font-bold text-blue-600">25</span> years of frac control expertise.
                  </p>
                  <p class="text-blueGray-600 leading-relaxed">
                     AMI builds control and instrumentation systems that operators trust—combining deep field experience with forward-thinking technology so your systems stay productive and reliable.
                  </p>
               </div>
            </div>
         </section>

         <!-- Closing CTA -->
         <section class="py-16 md:py-20 bg-blueGray-900">
            <div class="container mx-auto px-4 md:px-6">
               <div class="bg-blueGray-800 rounded-2xl p-8 md:p-12 border border-blueGray-700 text-center max-w-3xl mx-auto">
                  <h2 class="text-2xl md:text-3xl font-bold text-white mb-4">The future of pump control is here</h2>
                  <p class="text-blueGray-300 mb-8 max-w-xl mx-auto">
                     Embrace the redesigned Datavan MPC—pump control built for Pump Down jobs and full fleet operations.
                     Part of the broader
                     <router-link to="/wavecommand7" class="text-lightBlue-300 underline hover:text-lightBlue-200">WaveCommand7</router-link>
                     stack.
                  </p>
                  <div class="flex flex-wrap gap-3 justify-center">
                     <router-link
                        :to="{ path: '/', hash: '#contact' }"
                        class="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                     >
                        Book a Demo <i class="fas fa-arrow-right ml-2"></i>
                     </router-link>
                     <a href="#showcase" class="inline-block bg-transparent hover:bg-blueGray-700 text-white font-bold py-3 px-6 rounded-lg border border-blueGray-500 transition-colors duration-200">
                        See the New MPC
                     </a>
                  </div>
               </div>
            </div>
         </section>
      </main>
      <footer-component />
   </div>
</template>

<script>
import NavbarComponent from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import { setPageSeo, productJsonLd, breadcrumbJsonLd } from "@/utils/seo";
import heroImage from "@/assets/img/mpc/slide1-redesigned.png";
import remoteImage from "@/assets/img/mpc/slide3-remote.png";
import slotsImage from "@/assets/img/mpc/mpc-showcase.png";
import fleetImage from "@/assets/img/mpc/mpc-fleet.png";
import mobileImage from "@/assets/img/mpc/mpc-mobile.png";
import summaryImage from "@/assets/screenshots/fleetview.jpg";

export default {
   name: "DatavanMPC",
   components: {
      NavbarComponent,
      FooterComponent,
   },
   data() {
      return {
         heroImage,
         remoteImage,
         mobileImage,
         summaryImage,
         showcaseIndex: 0,
         showcaseVisible: true,
         showcaseTimer: null,
         showcaseFadeTimer: null,
         // Pump Down vs full-fleet screenshots for the showcase fade.
         showcaseSlides: [
            {
               image: slotsImage,
               label: "Pump Down · 1–2 pumps",
               alt: "Datavan MPC Pump Down pump control layout with a few pumps",
            },
            {
               image: fleetImage,
               label: "Full fleet · up to 40+ pumps",
               alt: "Datavan MPC full fleet multipump pump control interface",
            },
         ],
         // Operator capabilities grouped by category (merged left column).
         operatorFeatureGroups: [
            {
               category: "Control",
               colorClass: "text-lightBlue-300",
               categoryIcon: "fas fa-sliders-h",
               items: [
                  {
                     capability: "Remote control",
                     description: "Request remote per pump to unlock Start/Stop, RPM, gear, and kick-out controls.",
                  },
                  {
                     capability: "Speed, gear, and kick-out",
                     description: "Set engine RPM and transmission gear, and configure over-pressure trip (kick-out) per pump.",
                  },
               ],
            },
            {
               category: "Fleet",
               colorClass: "text-emerald-400",
               categoryIcon: "fas fa-broadcast-tower",
               items: [
                  {
                     capability: "Fleet-wide commands",
                     description: "Stop Engines, Neutral Idle All, Remote Enable All, plus total rate and well-head pressure readouts.",
                  },
                  {
                     capability: "Group control",
                     description: "Control pumps by group with shared RPM, gear, kick-out, and Neutral Idle Group.",
                  },
               ],
            },
            {
               category: "Monitoring",
               colorClass: "text-amber-300",
               categoryIcon: "fas fa-desktop",
               items: [
                  {
                     capability: "Alarms and diagnostics",
                     description: "Monitor active pump alarms and open Detail for engine, transmission, pump, and maintenance diagnostics.",
                  },
                  {
                     capability: "Multi-monitor layouts",
                     description: "Support for multiple monitors and resolutions—each screen can save its own layout for pump slots, group controls, and charting.",
                  },
               ],
            },
            {
               category: "Setup",
               colorClass: "text-cyan-300",
               categoryIcon: "fas fa-th-large",
               items: [
                  {
                     capability: "Pump slot assignment",
                     description: "Assign PumpLink, PumpLink7, or third-party pumps to slots and mirror the physical pad layout.",
                  },
                  {
                     capability: "Saved fleet layouts",
                     description: "Save each fleet’s layout and recall it instantly—or open and view it from the cloud, anywhere.",
                  },
               ],
            },
         ],
         // Six campaign feature pillars.
         features: [
            {
               title: "Intuitive UX",
               icon: "fas fa-desktop",
               text: "Easier-to-use interface optimized for larger resolutions and 4K monitors—crystal-clear overview of pump operations.",
               iconWrapClass: "bg-cyan-900 text-cyan-200 border-cyan-400",
            },
            {
               title: "Scalable Control",
               icon: "fas fa-layer-group",
               text: "Up to 20 pumps per 4K monitor—scale to 40+ pumps across a multi-monitor data van station.",
               iconWrapClass: "bg-blue-900 text-lightBlue-300 border-lightBlue-400",
            },
            {
               title: "Cloud & Fleet Monitoring",
               icon: "fas fa-cloud",
               text: "Streams operational data into WaveData Cloud for complete fleet monitoring—track system performance and health from anywhere.",
               iconWrapClass: "bg-indigo-900 text-indigo-300 border-indigo-400",
            },
            {
               title: "Remote Operation",
               icon: "fas fa-sliders-h",
               text: "WaveData Cloud supports remote pump operation—adjust and intervene without being physically on site.",
               iconWrapClass: "bg-amber-900 text-amber-300 border-amber-400",
            },
            {
               title: "Smart Control Commands",
               icon: "fas fa-user-lock",
               text: "Any number of browser clients can connect and view, but only one client at a time can take control and run the pumps.",
               iconWrapClass: "bg-emerald-900 text-emerald-300 border-emerald-500",
            },
            {
               title: "Future-Proof",
               icon: "fas fa-sync-alt",
               text: "Works with new WaveEdge and PumpLink7 devices and existing legacy pumps for a smooth transition.",
               iconWrapClass: "bg-orange-900 text-orange-300 border-orange-400",
            },
         ],
         // Value props from remote-control campaign slide.
         remoteProps: [
            { title: "Up to 20 Pumps Per 4K Monitor", icon: "fas fa-tv" },
            { title: "Remote Pump Operation", icon: "fas fa-industry" },
            { title: "Real-Time Fleet Insights", icon: "fas fa-chart-bar" },
            { title: "Cloud & Fleet Monitoring", icon: "fas fa-cloud" },
         ],
         // Platform pillars with product deep-links.
         platformPillars: [
            {
               title: "WaveEdge",
               subtitle: "Modern Linux runtime",
               text: "Industrial edge runtime for acquisition, control, and secure connectivity.",
               to: "/waveedge",
               icon: "fas fa-heartbeat",
               iconWrapClass: "bg-emerald-900 text-emerald-300 border-emerald-500",
            },
            {
               title: "PumpLink7",
               subtitle: "Rugged hardware",
               text: "Ruggedized pump control hardware with the same operator experience as the AMI ecosystem.",
               to: "/pumplink7",
               icon: "fas fa-microchip",
               iconWrapClass: "bg-amber-900 text-amber-300 border-amber-400",
            },
            {
               title: "WaveData Cloud",
               subtitle: "Cloud SaaS",
               text: "Fleet monitoring, remote visibility, and cloud-enabled pump operation.",
               to: "/wavedata",
               icon: "fas fa-cloud",
               iconWrapClass: "bg-blue-900 text-lightBlue-300 border-lightBlue-400",
            },
         ],
      };
   },
   computed: {
      // Flatten groups into rows with rowspan metadata for the merged category column.
      operatorFeatureRows() {
         const rows = [];
         this.operatorFeatureGroups.forEach((group) => {
            group.items.forEach((item, idx) => {
               rows.push({
                  key: `${group.category}-${item.capability}`,
                  category: group.category,
                  colorClass: group.colorClass,
                  categoryIcon: group.categoryIcon,
                  showCategory: idx === 0,
                  rowspan: group.items.length,
                  capability: item.capability,
                  description: item.description,
               });
            });
         });
         return rows;
      },
   },
   mounted() {
      setPageSeo({
         title: "Datavan MPC | Pump Control for Full Fleets | AMI",
         description:
            "Multipump pump control for Pump Down and full fleets—scale from a few pumps to 40+ across multiple monitors with WaveData Cloud.",
         keywords:
            "pump control, Pump Down, Datavan MPC, multipump control, frac pump control, AMI",
         path: "/mpc",
         jsonLd: [
            productJsonLd({
               name: "Datavan MPC",
               description:
                  "Multipump pump control for Pump Down and full fleet frac operations.",
               path: "/mpc",
            }),
            breadcrumbJsonLd([
               { name: "Home", path: "/" },
               { name: "Datavan MPC", path: "/mpc" },
            ]),
         ],
      });
      this.startShowcaseFade();
   },
   beforeUnmount() {
      if (this.showcaseTimer) clearInterval(this.showcaseTimer);
      if (this.showcaseFadeTimer) clearTimeout(this.showcaseFadeTimer);
   },
   methods: {
      // Alternate screenshots: fade out, swap, fade in (never stack both).
      startShowcaseFade() {
         this.showcaseTimer = setInterval(() => {
            this.advanceShowcase();
         }, 5000);
      },
      advanceShowcase() {
         this.showcaseVisible = false;
         if (this.showcaseFadeTimer) clearTimeout(this.showcaseFadeTimer);
         this.showcaseFadeTimer = setTimeout(() => {
            this.showcaseIndex = (this.showcaseIndex + 1) % this.showcaseSlides.length;
            this.showcaseVisible = true;
         }, 700);
      },
   },
};
</script>
